import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSlaConfig } from '@/hooks/useManagerDashboard';
import { useCanActAsManager } from '@/hooks/useCanActAsManager';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save, Loader2, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface EditedConfig {
  [id: string]: {
    first_response_minutes?: number;
    resolution_minutes?: number;
    internal_escalation_minutes?: number;
    is_active?: boolean;
  };
}

const DEMAND_TYPE_LABELS: Record<string, string> = {
  status: 'Status do Processo',
  documents: 'Documentos',
  doubt: 'Dúvida',
  financial: 'Financeiro',
  urgent: 'Urgente',
  other: 'Outro',
};

const PRIORITY_LABELS: Record<string, string> = {
  normal: 'Normal',
  urgent: 'Urgente',
};

export default function SLAConfigSettings() {
  const navigate = useNavigate();
  const { canActAsManager, isLoading: roleLoading } = useCanActAsManager();
  const { configs, isLoading, updateConfig, refetch } = useSlaConfig();
  const [editedConfigs, setEditedConfigs] = useState<EditedConfig>({});
  const [saving, setSaving] = useState(false);

  // Redirect if not manager
  useEffect(() => {
    if (!roleLoading && !canActAsManager) {
      navigate('/settings');
    }
  }, [roleLoading, canActAsManager, navigate]);

  const hasChanges = Object.keys(editedConfigs).length > 0;

  const handleValueChange = (id: string, field: string, value: number | boolean) => {
    setEditedConfigs(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const getEditedValue = (id: string, field: string, originalValue: number | boolean) => {
    if (editedConfigs[id] && editedConfigs[id][field as keyof typeof editedConfigs[string]] !== undefined) {
      return editedConfigs[id][field as keyof typeof editedConfigs[string]];
    }
    return originalValue;
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    setSaving(true);
    try {
      const updates = Object.entries(editedConfigs);
      
      for (const [id, changes] of updates) {
        await updateConfig(id, changes);
      }

      setEditedConfigs({});
      toast.success('Configurações de SLA atualizadas com sucesso');
      refetch();
    } catch (error) {
      console.error('Error saving SLA config:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const formatMinutesToDisplay = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  if (roleLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings/operations')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">Configuração de SLA</h1>
            <p className="text-sm text-muted-foreground">
              Defina metas de tempo de resposta e resolução por tipo de demanda
            </p>
          </div>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={!hasChanges || saving}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Salvar
          {hasChanges && (
            <Badge variant="secondary" className="ml-2">
              {Object.keys(editedConfigs).length}
            </Badge>
          )}
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Como funciona o SLA</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300">
                <li><strong>1ª Resposta:</strong> Tempo máximo para o primeiro atendimento</li>
                <li><strong>Resolução:</strong> Tempo máximo para resolver a sessão</li>
                <li><strong>Escalation:</strong> Tempo para escalar internamente se não houver resposta</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Config Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Metas por Tipo de Demanda</CardTitle>
          <CardDescription>
            Clique nos valores para editar. Todos os tempos são em minutos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo de Demanda</TableHead>
                <TableHead>Prioridade</TableHead>
                <TableHead className="text-center">1ª Resposta (min)</TableHead>
                <TableHead className="text-center">Resolução (min)</TableHead>
                <TableHead className="text-center">Escalation (min)</TableHead>
                <TableHead className="text-center">Ativo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.map((config) => {
                const isEdited = !!editedConfigs[config.id];
                return (
                  <TableRow key={config.id} className={cn(isEdited && "bg-amber-50 dark:bg-amber-950/20")}>
                    <TableCell className="font-medium">
                      {DEMAND_TYPE_LABELS[config.demand_type] || config.demand_type}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline"
                        className={cn(
                          config.priority === 'urgent' 
                            ? "border-red-500 text-red-600" 
                            : "border-slate-300 text-slate-600"
                        )}
                      >
                        {PRIORITY_LABELS[config.priority] || config.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <EditableCell
                        value={getEditedValue(config.id, 'first_response_minutes', config.first_response_minutes) as number}
                        onChange={(v) => handleValueChange(config.id, 'first_response_minutes', v)}
                        displayValue={formatMinutesToDisplay(
                          getEditedValue(config.id, 'first_response_minutes', config.first_response_minutes) as number
                        )}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <EditableCell
                        value={getEditedValue(config.id, 'resolution_minutes', config.resolution_minutes) as number}
                        onChange={(v) => handleValueChange(config.id, 'resolution_minutes', v)}
                        displayValue={formatMinutesToDisplay(
                          getEditedValue(config.id, 'resolution_minutes', config.resolution_minutes) as number
                        )}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <EditableCell
                        value={getEditedValue(config.id, 'internal_escalation_minutes', config.internal_escalation_minutes) as number}
                        onChange={(v) => handleValueChange(config.id, 'internal_escalation_minutes', v)}
                        displayValue={formatMinutesToDisplay(
                          getEditedValue(config.id, 'internal_escalation_minutes', config.internal_escalation_minutes) as number
                        )}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={getEditedValue(config.id, 'is_active', config.is_active) as boolean}
                        onCheckedChange={(v) => handleValueChange(config.id, 'is_active', v)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Unsaved changes warning */}
      {hasChanges && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
          <Card className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/50 shadow-lg">
            <CardContent className="p-3 flex items-center gap-3">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-800 dark:text-amber-200">
                Você tem alterações não salvas
              </span>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Salvar agora'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// Editable Cell Component
interface EditableCellProps {
  value: number;
  onChange: (value: number) => void;
  displayValue: string;
}

function EditableCell({ value, onChange, displayValue }: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value.toString());

  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const handleBlur = () => {
    const numValue = parseInt(localValue, 10);
    if (!isNaN(numValue) && numValue >= 1) {
      if (numValue !== value) {
        onChange(numValue);
      }
    } else {
      setLocalValue(value.toString());
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setLocalValue(value.toString());
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <Input
        type="number"
        min={1}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="w-20 h-8 text-center mx-auto"
        autoFocus
      />
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className="px-2 py-1 rounded hover:bg-muted transition-colors text-sm font-mono"
    >
      {displayValue}
    </button>
  );
}